
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints and webhooks associated with the Plaid Virtual Accounts product.',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Virtual Accounts (UK and Europe)' subtitle='API reference for Virtual Accounts endpoints and webhooks' compact mdxType="Header" />
    <p>{`Manage the entire lifecycle of a payment. For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/"
      }}>{`Virtual Accounts documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/wallet/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a virtual account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/wallet/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fetch a virtual account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/wallet/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List all virtual accounts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/wallet/transaction/execute`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Execute a transaction`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/wallet/transaction/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fetch a transaction`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/wallet/transaction/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List all transactions`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`See also`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/payment_initiation/payment/reverse`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Refund a payment from a virtual account`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Webhooks`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#wallet_transaction_status_update"
            }}><inlineCode parentName="a">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The status of a transaction has changed`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "walletcreate"
    }}>{`/wallet/create`}</h4>
    <Schema method='POST' route='/wallet/create' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/wallet/create' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewWalletCreateRequest(isoCurrencyCode)
response, _, err := client.PlaidApi.WalletCreate(ctx).WalletCreateRequest(*request).Execute()
walletID := response.GetWalletId()
balance := response.GetBalance()
numbers := response.GetNumbers()
recipientID := response.GetRecipientId()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.WalletCreateRequest{IsoCurrencyCode: isoCurrencyCode}
response, err := client.Wallet.Create(ctx, request)
walletID := response.GetWalletID()
balance := response.GetBalance()
numbers := response.GetNumbers()
recipientID := response.GetRecipientID()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`WalletCreateRequest request = new WalletCreateRequest()
  .isoCurrencyCode(isoCurrencyCode);
Response<WalletCreateResponse> response = client()
  .walletCreate(request)
  .execute();
String walletId = response.body().getWalletId();
WalletBalance balance = response.body().getBalance();
WalletNumbers numbers = response.body().getNumbers();
String recipientId = response.body().getRecipientId();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::WalletCreateRequest.new({ iso_currency_code: iso_currency_code })
response = client.wallet_create(request)
wallet_id = response.wallet_id
balance = response.balance
numbers = response.numbers
recipient_id = response.recipient_id
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: WalletCreateRequest = {
  iso_currency_code: isoCurrencyCode,
};
try {
  const response = await plaidClient.walletCreate(request);
  const walletID = response.data.wallet_id;
  const balance = response.data.balance;
  const numbers = response.data.numbers;
  const recipientID = response.data.recipient_id;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/wallet/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "iso_currency_code": "\${ISO_CURRENCY_CODE}"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = WalletCreateRequest(iso_currency_code=WalletISOCurrencyCode(iso_currency_code))
response = client.wallet_create(request)
wallet_id = response['wallet_id']
balance = response['balance']
numbers = response['numbers']
recipient_id = response['recipient_id']
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/wallet/create' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "walletget"
    }}>{`/wallet/get`}</h4>
    <Schema method='POST' route='/wallet/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/wallet/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewWalletGetRequest(walletID)
response, _, err := client.PlaidApi.WalletGet(ctx).WalletGetRequest(*request).Execute()
walletID := response.GetWalletId()
balance := response.GetBalance()
numbers := response.GetNumbers()
recipientID := response.GetRecipientId()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.WalletGetRequest{WalletID: walletID}
response, err := client.Wallet.Get(ctx, request)
walletID := response.GetWalletID()
balance := response.GetBalance()
numbers := response.GetNumbers()
recipientID := response.GetRecipientID()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`WalletGetRequest request = new WalletGetRequest()
  .walletId(walletId);
Response<WalletGetResponse> response = client()
  .walletGet(request)
  .execute();
String walletId = response.body().getWalletId();
WalletBalance balance = response.body().getBalance();
WalletNumbers numbers = response.body().getNumbers();
String recipientID = response.body().getRecipientId();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::WalletGetRequest.new({ wallet_id: wallet_id })
response = client.wallet_get(request)
wallet_id = response.wallet_id
balance = response.balance
numbers = response.numbers
recipient_id = response.recipient_id
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: WalletGetRequest = {
  wallet_id: walletID,
};
try {
  const response = await plaidClient.walletGet(request);
  const walletID = response.data.wallet_id;
  const balance = response.data.balance;
  const numbers = response.data.numbers;
  const recipientID = response.data.recipient_id;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/wallet/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "wallet_id": "\${WALLET_ID}"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = WalletGetRequest(wallet_id=wallet_id)
response = client.wallet_get(request)
wallet_id = response['wallet_id']
balance = response['balance']
numbers = response['numbers']
recipient_id = response['recipient_id']
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/wallet/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "walletlist"
    }}>{`/wallet/list`}</h4>
    <Schema method='POST' route='/wallet/list' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/wallet/list' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewWalletListRequest()
request.SetIsoCurrencyCode(plaid.WALLETISOCURRENCYCODE_GBP)
request.SetCount(10)
response, _, err := client.PlaidApi.WalletList(ctx).WalletListRequest(*request).Execute()
wallets := response.GetWallets()
nextCursor := response.GetNextCursor()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.WalletListRequest{}
request.IsoCurrencyCode = plaid.WalletIsoCurrencyCode(plaid.WalletIsoCurrencyCodeGbp).Ptr()
request.Count = plaid.Int(10)
response, err := client.Wallet.List(ctx, request)
var wallets []*plaid.Wallet
var nextCursor *string
if response != nil {
    wallets = response.Response.GetWallets()
    nextCursor = response.Response.GetNextCursor()
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`WalletListRequest request = new WalletListRequest()
  .isoCurrencyCode(WalletISOCurrencyCode.GBP)
  .count(10);
Response<WalletListResponse> response = client()
  .walletList(request)
  .execute();
List<Wallet> wallets = response.body().getWallets();
String nextCursor = response.body().getNextCursor();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::WalletListRequest.new(
  {
    iso_currency_code: 'GBP',
    count: 10
  }
)
response = client.wallet_list(request)
wallets = response.wallets
next_cursor = response.next_cursor
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: WalletListRequest = {
  iso_currency_code: 'GBP',
  count: 10,
};
try {
  const response = await plaidClient.walletList(request);
  const wallets = response.data.wallets;
  const nextCursor = response.data.next_cursor;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/wallet/list \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "iso_currency_code": "GBP",
  "count": 10
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = WalletListRequest(
  iso_currency_code=WalletISOCurrencyCode('GBP'),
  count=10
)
response = client.wallet_list(request)
wallets = response['wallets']
next_cursor = response['next_cursor']
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/wallet/list' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "wallettransactionexecute"
    }}>{`/wallet/transaction/execute`}</h4>
    <Schema method='POST' route='/wallet/transaction/execute' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/wallet/transaction/execute' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`counterpartyBACS := plaid.NewWalletTransactionCounterpartyBACS()
counterpartyBACS.SetAccount("12345678")
counterpartyBACS.SetSortCode("123456")

request := plaid.NewWalletTransactionExecuteRequest(
  "39fae5f2-b2b4-48b6-a363-5328995b2753",
  walletID,
  plaid.WalletTransactionCounterparty{
    Name: "Test",
    Numbers: plaid.WalletTransactionCounterpartyNumbers{
      Bacs: counterpartyBACS,
    },
  },
  plaid.WalletTransactionAmount{
    Value: 1.00,
    IsoCurrencyCode: plaid.WALLETISOCURRENCYCODE_GBP,
  },
  "TransactionABC123",
)
request.SetUserId(userID)
response, _, err := client.PlaidApi.WalletTransactionExecute(ctx).WalletTransactionExecuteRequest(*request).Execute()
transactionID := response.GetTransactionId()
status := response.GetStatus()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`counterpartyBACS := &plaid.WalletTransactionCounterpartyBacs{}
counterpartyBACS.Account = plaid.String("12345678")
counterpartyBACS.SortCode = plaid.String("123456")

request := &plaid.WalletTransactionExecuteRequest{
    IdempotencyKey: "39fae5f2-b2b4-48b6-a363-5328995b2753",
    WalletID:       walletID,
    Counterparty: &plaid.WalletTransactionCounterparty{
        Name:    "Test",
        Numbers: &plaid.WalletTransactionCounterpartyNumbers{Bacs: counterpartyBACS},
    },
    Amount: &plaid.WalletTransactionAmount{
        Value:           1.00,
        IsoCurrencyCode: plaid.WalletIsoCurrencyCodeGbp,
    },
    Reference: "TransactionABC123",
}
request.UserID = &userID
response, err := client.Wallet.ExecuteTransaction(ctx, request)
transactionID := response.GetTransactionID()
status := response.GetStatus()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`WalletTransactionCounterparty counterparty = new WalletTransactionCounterparty()
  .name("Test")
  .numbers(new WalletTransactionCounterpartyNumbers()
    .bacs(new WalletTransactionCounterpartyBACS()
      .account("12345678")
      .sortCode("123456")
    )
  );
WalletTransactionAmount amount = new WalletTransactionAmount()
  .value(1.00)
  .isoCurrencyCode(WalletISOCurrencyCode.GBP);

WalletTransactionExecuteRequest request = new WalletTransactionExecuteRequest()
  .walletId(walletId)
  .userId(userId)
  .counterparty(counterparty)
  .amount(amount)
  .reference("TransactionABC123")
  .idempotencyKey("39fae5f2-b2b4-48b6-a363-5328995b2753");

Response<WalletTransactionExecuteResponse> response = client()
  .walletTransactionExecute(request)
  .execute();
String transactionId = response.body().getTransactionId();
WalletTransactionStatus status = response.body().getStatus();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::WalletTransactionExecuteRequest.new(
  {
    wallet_id: wallet_id,
    user_id: user_id,
    counterparty: {
      name: 'Test',
      numbers: {
        bacs: {
          account: '12345678',
          sort_code: '123456'
        }
      }
    },
    amount: {
      value: 1,
      iso_currency_code: 'GBP'
    },
    reference: 'TransactionABC123',
    idempotency_key: '39fae5f2-b2b4-48b6-a363-5328995b2753'
  }
)
response = client.wallet_transaction_execute(request)
transaction_id = response.transaction_id
status = response.status
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: WalletTransactionExecuteRequest = {
  wallet_id: walletID,
  user_id: userID,
  counterparty: {
    name: 'Test',
    numbers: {
      bacs: {
        account: '12345678',
        sort_code: '123456',
      },
    },
  },
  amount: {
    value: 1,
    iso_currency_code: 'GBP',
  },
  reference: 'TransactionABC123',
  idempotency_key: '39fae5f2-b2b4-48b6-a363-5328995b2753',
};
try {
  const response = await plaidClient.walletTransactionExecute(request);
  const transactionID = response.data.transaction_id;
  const status = response.data.status;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/wallet/transaction/execute \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "wallet_id": "\${WALLET_ID}",
  "user_id": "\${USER_ID}",
  "counterparty": {
    "name": "Test",
    "numbers": {
      "bacs": {
        "account": "12345678",
        "sort_code": "123456"
      }
    }
  },
  "amount": {
    "value": 1,
    "iso_currency_code": "GBP"
  },
  "reference": "TransactionABC123",
  "idempotency_key": "39fae5f2-b2b4-48b6-a363-5328995b2753"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = WalletTransactionExecuteRequest(
  wallet_id=wallet_id,
  user_id=user_id,
  counterparty=WalletTransactionCounterparty(
    name='Test',
    numbers=WalletTransactionCounterpartyNumbers(
      bacs=WalletTransactionCounterpartyBACS(
        account='12345678',
        sort_code='123456'
      )
    )
  ),
  amount=WalletTransactionAmount(
    value=1.0,
    iso_currency_code=WalletISOCurrencyCode('GBP')
  ),
  reference='TransactionABC123',
  idempotency_key=WalletTransactionIdempotencyKey('39fae5f2-b2b4-48b6-a363-5328995b2753')
)
response = client.wallet_transaction_execute(request)
transaction_id = response['transaction_id']
status = response['status']
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/wallet/transaction/execute' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "wallettransactionget"
    }}>{`/wallet/transaction/get`}</h4>
    <Schema method='POST' route='/wallet/transaction/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/wallet/transaction/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewWalletTransactionGetRequest(transactionID)
response, _, err := client.PlaidApi.WalletTransactionGet(ctx).WalletTransactionGetRequest(*request).Execute()
transactionID := response.GetTransactionId()
reference := response.GetReference()
txType := response.GetType()
amount := response.GetAmount()
counterparty := response.GetCounterparty()
status := response.GetStatus()
createdAt := response.GetCreatedAt()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.WalletTransactionGetRequest{TransactionID: transactionID}
response, err := client.Wallet.GetTransaction(ctx, request)
transactionID := response.GetTransactionID()
reference := response.GetReference()
txType := response.GetType()
amount := response.GetAmount()
counterparty := response.GetCounterparty()
status := response.GetStatus()
createdAt := response.GetCreatedAt()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`WalletTransactionGetRequest request = new WalletTransactionGetRequest()
  .transactionId(transactionId);
Response<WalletTransactionGetResponse> response = client()
  .walletTransactionGet(request)
  .execute();
String transactionId = response.body().getTransactionId();
String reference = response.body().getReference();
WalletTransactionGetResponse.TypeEnum type = response.body().getType();
WalletTransactionAmount amount = response.body().getAmount();
WalletTransactionCounterparty counterparty = response.body().getCounterparty();
WalletTransactionStatus status = response.body().getStatus();
OffsetDateTime createdAt = response.body().getCreatedAt();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::WalletTransactionGetRequest.new({ transaction_id: transaction_id })
response = client.wallet_transaction_get(request)
transaction_id = response.transaction_id
reference = response.reference
type = response.type
amount = response.amount
counterparty = response.counterparty
status = response.status
created_at = response.created_at
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: WalletTransactionGetRequest = {
  transaction_id: transactionID,
};
try {
  const response = await plaidClient.walletTransactionGet(request);
  const transactionID = response.data.transaction_id;
  const reference = response.data.reference;
  const type = response.data.type;
  const amount = response.data.amount;
  const counterparty = response.data.counterparty;
  const status = response.data.status;
  const createdAt = response.data.created_at;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/wallet/transaction/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "transaction_id": "\${TRANSACTION_ID}"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = WalletTransactionGetRequest(transaction_id=transaction_id)
response = client.wallet_transaction_get(request)
transaction_id = response['transaction_id']
reference = response['reference']
type = response['type']
amount = response['amount']
counterparty = response['counterparty']
status = response['status']
created_at = response['created_at']
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/wallet/transaction/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "wallettransactionlist"
    }}>{`/wallet/transaction/list`}</h4>
    <Schema method='POST' route='/wallet/transaction/list' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/wallet/transaction/list' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewWalletTransactionListRequest(walletID)
request.SetCount(10)
response, _, err := client.PlaidApi.WalletTransactionList(ctx).WalletTransactionListRequest(*request).Execute()
transactions := response.GetTransactions()
nextCursor := response.GetNextCursor()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.WalletTransactionListRequest{WalletID: walletID}
request.Count = plaid.Int(10)
response, err := client.Wallet.ListTransactions(ctx, request)
var transactions []*plaid.WalletTransaction
var nextCursor *string
if response != nil {
    transactions = response.Response.GetTransactions()
    nextCursor = response.Response.GetNextCursor()
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`WalletTransactionListRequest request = new WalletTransactionListRequest()
  .walletId(walletId)
  .count(10);
Response<WalletTransactionListResponse> response = client()
  .walletTransactionList(request)
  .execute();
List<WalletTransaction> transactions = response.body().getTransactions();
String nextCursor = response.body().getNextCursor();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::WalletTransactionListRequest.new(
  {
    wallet_id: wallet_id,
    count: 10
  }
)
response = client.wallet_transaction_list(request)
transactions = response.transactions
next_cursor = response.next_cursor
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: WalletTransactionListRequest = {
  wallet_id: walletID,
  count: 10,
};
try {
  const response = await plaidClient.walletTransactionList(request);
  const transactions = response.data.transactions;
  const nextCursor = response.data.next_cursor;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/wallet/transaction/list \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "wallet_id": "\${WALLET_ID}",
  "count": 10
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = WalletTransactionListRequest(
  wallet_id=wallet_id,
  count=10
)
response = client.wallet_transaction_list(request)
transactions = response['transactions']
next_cursor = response['next_cursor']
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/wallet/transaction/list' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <p>{`Updates are sent to indicate that the status of transaction has changed. All virtual account webhooks have a `}<inlineCode parentName="p">{`webhook_type`}</inlineCode>{` of `}<inlineCode parentName="p">{`WALLET`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "wallet_transaction_status_update"
    }}><inlineCode parentName="h4">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode></h4>
    <Schema model='WalletTransactionStatusUpdateWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "walletcreate",
        level: 4,
        title: "/wallet/create",
      },
{
        id: "walletget",
        level: 4,
        title: "/wallet/get",
      },
{
        id: "walletlist",
        level: 4,
        title: "/wallet/list",
      },
{
        id: "wallettransactionexecute",
        level: 4,
        title: "/wallet/transaction/execute",
      },
{
        id: "wallettransactionget",
        level: 4,
        title: "/wallet/transaction/get",
      },
{
        id: "wallettransactionlist",
        level: 4,
        title: "/wallet/transaction/list",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "wallet_transaction_status_update",
        level: 4,
        title: "WALLET_TRANSACTION_STATUS_UPDATE",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
